import {
  isShallowEqual,
  validateAndSubmitSearchForm,
  expandAccordionsByErrorPaths,
  expandAllAccordions,
} from '@frontend/pricing-search';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { UseFormReturn } from 'react-hook-form';
import { shallowEqual, useSelector } from 'react-redux';
import {
  formValidationsSelector,
  originalLoanInfoSelector,
  searchFormSelector,
} from '../../../../store/selectors';

export const useFormValidationsSelector = (pathValue) =>
  useSelector(formValidationsSelector(pathValue), isShallowEqual);

export const useInitialValuesSelector = (pathValue) =>
  useSelector(formValidationsSelector(pathValue), isShallowEqual);

export const useSearchFormSelector = (pathValue) =>
  useSelector(searchFormSelector(pathValue), isShallowEqual);

export const useOriginalLoanInfoSelector = (pathValue, defaultValue?: any) =>
  useSelector(originalLoanInfoSelector(pathValue, defaultValue), shallowEqual);

export const useExpandAccordionsOnError = ({
  formReady,
  schemaWithLookups,
  schemaForm,
  accordionFieldMap,
  setActiveAccordions,
}: {
  formReady: boolean;
  schemaWithLookups: any;
  schemaForm: UseFormReturn;
  accordionFieldMap: Record<number, string[]>;
  setActiveAccordions: Dispatch<SetStateAction<Record<number, boolean>>>;
}) => {
  const errorFilters = (useSearchFormSelector('errorForm.errorFilters') as any)
    ?.value;

  const errorFiltersRef = useRef(errorFilters);
  errorFiltersRef.current = errorFilters;
  const schemaWithLookupsRef = useRef(schemaWithLookups);
  schemaWithLookupsRef.current = schemaWithLookups;
  const accordionFieldMapRef = useRef(accordionFieldMap);
  accordionFieldMapRef.current = accordionFieldMap;

  const runValidationAndOpenAccordions = useCallback(() => {
    validateAndSubmitSearchForm({
      schema: schemaWithLookupsRef.current,
      schemaForm,
      preview: false,
    })
      .then((result) => {
        if (result.errorPaths?.length) {
          const toOpen = expandAccordionsByErrorPaths(
            accordionFieldMapRef.current,
            result.errorPaths,
          );
          setActiveAccordions((prev) => ({ ...prev, ...toOpen }));
        } else if (!result.valid) {
          setActiveAccordions((prev) => ({
            ...prev,
            ...expandAllAccordions(accordionFieldMapRef.current),
          }));
        }
      })
      .catch((err) => {
        console.error('useExpandAccordionsOnError: validation failed', err);
      });
  }, []);

  useEffect(() => {
    if (!errorFilters?.length || !formReady) return;
    runValidationAndOpenAccordions();
  }, [errorFilters, formReady]);

  const handleSearchFormInitialized = useCallback(() => {
    if (!errorFiltersRef.current?.length) return;
    runValidationAndOpenAccordions();
  }, []);

  return { handleSearchFormInitialized };
};
